/**
 * @fileoverview Gather Phase Constants
 *
 * Purpose: Shared constants for the Gather Information phase UI.
 * Category labels, filter keys, accepted file types, and status labels.
 *
 * Used by:
 * - All gather phase components
 */

import {
  CATEGORY_LABELS as BACKEND_CATEGORY_LABELS,
  CATEGORY_CONFIG,
} from "@cvx/lib/documentCategories";

// Re-export for components that need it
export { CATEGORY_CONFIG };

// ---------------------------------------------------------------------------
// File Upload
// ---------------------------------------------------------------------------

/**
 * Accepted MIME types / extensions for document upload.
 * Individual emails (.eml/.msg) are accepted (MOR-899); mailbox archives
 * (.pst) are not — the server rejects bulk email imports.
 */
export const ACCEPTED_FILE_TYPES = [
  ".pdf,application/pdf",
  ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xls,application/vnd.ms-excel",
  ".csv,text/csv",
  ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".doc,application/msword",
  ".jpg,.jpeg,image/jpeg",
  ".png,image/png",
  ".msg,application/vnd.ms-outlook",
  ".eml,message/rfc822",
  ".txt,text/plain",
].join(",");

// ---------------------------------------------------------------------------
// Category Labels & Filters
// ---------------------------------------------------------------------------

/** Labels for top-level categories including "all" filter option */
export const CATEGORY_LABELS: Record<string, string> = {
  all: "All Documents",
  ...BACKEND_CATEGORY_LABELS,
};

/**
 * Status filter key for documents whose processing pipeline failed.
 * Not a document category — excluded from category pickers.
 */
export const FAILED_FILTER_KEY = "failed";
export const FAILED_FILTER_LABEL = "Failed Documents";

/** Ordered list of category filter keys for the filter bar */
export const CATEGORY_FILTER_KEYS = [
  "all",
  "corporate_documents",
  "director_disclosures",
  "assets",
  "security_interests",
  "bank_statements_transactions",
  "financial_ledgers",
  "general_ledgers",
  "forecasts_budget",
  "general_liabilities",
  "contracts",
  "invoices",
  "employment",
  "tax_and_sro",
  "proof_of_debt",
  "loans",
  "legal_disputes",
  "insurance",
  "firm_generated",
  "other",
] as const;

// ---------------------------------------------------------------------------
// Processing Status
// ---------------------------------------------------------------------------

/** Human-readable labels for pipeline processing stages */
export const PROCESSING_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  extracting: "Extracting",
  extracted: "Extracted",
  classifying: "Classifying",
  classified: "Classified",
  indexing: "Indexing",
  indexed: "Indexed",
  failed: "Failed",
};

/**
 * Derives the overall document processing status from individual stage statuses.
 *
 * @returns One of: "pending" | "processing" | "ready" | "failed"
 */
export function getOverallStatus(
  extractionStatus?: string,
  classificationStatus?: string,
  indexingStatus?: string,
  categoryExtractionStatus?: string,
): "pending" | "processing" | "ready" | "failed" {
  // Any failure means failed
  if (
    extractionStatus === "failed" ||
    classificationStatus === "failed" ||
    indexingStatus === "failed" ||
    categoryExtractionStatus === "failed"
  ) {
    return "failed";
  }

  // All complete means ready (categoryExtractionStatus is optional for legacy docs)
  if (
    extractionStatus === "extracted" &&
    classificationStatus === "classified" &&
    indexingStatus === "indexed" &&
    (!categoryExtractionStatus || categoryExtractionStatus === "completed")
  ) {
    return "ready";
  }

  // Any in-progress means processing
  if (
    extractionStatus === "extracting" ||
    classificationStatus === "classifying" ||
    indexingStatus === "indexing" ||
    categoryExtractionStatus === "extracting" ||
    categoryExtractionStatus === "pending"
  ) {
    return "processing";
  }

  return "pending";
}
